/**
 * 文件類型與副檔名（伺服器與前端共用）。
 *
 * 圖說管理方向：上傳的檔案原則上都要保留。認識的類型走對應能力
 * （drawing／model 轉檔、PDF／點陣圖預覽、Word／Excel／PowerPoint 轉 PDF 預覽、.url 網頁）；
 * 不認識的歸為 `other`，只保存與下載、不開 Viewer。
 *
 * 仍拒絕明顯不安全的格式（執行檔、網頁、腳本等），避免入庫後被當成可執行／可嵌入內容。
 */
export const KIND_EXT = {
  drawing: ['.dwg', '.dxf', '.dgn', '.dwf'],
  model: ['.rvt', '.rfa'],
  pdf: ['.pdf'],
  office: ['.doc', '.docx', '.xls', '.xlsx', '.xlsm', '.csv', '.ppt', '.pptx', '.odt', '.ods', '.odp', '.rtf', '.txt', '.md'],
  image: ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.tif', '.tiff', '.heic'],
  attachment: ['.ifc', '.nwd', '.nwc', '.skp', '.3dm', '.msg', '.eml', '.7z', '.rar'],
  web: ['.url'],
  sheetset: ['.dst'],
};

/** 可在 Viewer 內預覽的點陣圖副檔名（不含 HEIC：多數瀏覽器無法解碼）。 */
export const IMAGE_PREVIEW_EXTS = new Set(['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.tif', '.tiff']);

/** 開啟時以 LibreOffice 轉成 PDF 再預覽。txt／md 仍只下載。 */
export const OFFICE_PREVIEW_EXTS = new Set([
  '.doc', '.docx', '.odt', '.rtf',
  '.xls', '.xlsx', '.xlsm', '.ods', '.csv',
  '.ppt', '.pptx', '.odp',
]);

/** 明確拒絕入庫的副檔名（不分大小寫）。 */
export const DENIED_EXTENSIONS = new Set([
  '.exe', '.bat', '.cmd', '.com', '.msi', '.scr', '.pif', '.cpl',
  '.dll', '.sys', '.drv',
  '.ps1', '.vbs', '.vbe', '.js', '.jse', '.mjs', '.cjs', '.wsf', '.wsh',
  '.sh', '.bash', '.zsh', '.command',
  '.html', '.htm', '.xhtml', '.shtml', '.svg', '.svgz',
  '.php', '.asp', '.aspx', '.jsp', '.cgi',
  '.jar', '.apk', '.app', '.dmg', '.pkg',
]);

export const KIND_LABEL = {
  drawing: '圖面',
  model: '模型',
  pdf: 'PDF',
  office: 'Office',
  image: '圖片',
  attachment: '附件',
  web: '網頁',
  sheetset: '圖紙集',
  other: '其他',
};

const EXT_KIND = new Map(Object.entries(KIND_EXT).flatMap(([kind, exts]) => exts.map(ext => [ext, kind])));

/** 已歸類的副檔名（不含 other／拒絕清單）。 */
export const ALLOWED_EXTENSIONS = [...EXT_KIND.keys()];

export const isDeniedExt = (ext) => DENIED_EXTENSIONS.has(String(ext || '').toLowerCase());

/**
 * @returns {'drawing'|'model'|'pdf'|'office'|'image'|'attachment'|'web'|'sheetset'|'other'|null}
 *   null = 拒絕入庫；other = 保留但不開啟。
 */
export const kindOfExt = (ext) => {
  const e = String(ext || '').toLowerCase();
  if (isDeniedExt(e)) return null;
  if (!e) return 'other';
  return EXT_KIND.get(e) ?? 'other';
};

export const kindOfName = (name) => {
  const m = /\.[^./\\]+$/.exec(String(name || ''));
  // 無副檔名 → other；有副檔名則依 kindOfExt（可能為 null＝拒絕）
  if (!m) return String(name || '').trim() ? 'other' : null;
  return kindOfExt(m[0]);
};

/** 需要轉檔的類型。 */
export const CONVERTIBLE_KINDS = new Set(['drawing', 'model']);

/** 可在 Viewer 內開啟的類型；其餘只下載。圖片另見 isImagePreviewable（HEIC 仍只下載）。 */
export const VIEWABLE_KINDS = new Set(['drawing', 'model', 'pdf', 'image', 'web', 'sheetset']);

/** 清冊「文件」分頁的類型。可轉 PDF 的 Office 仍可開啟；txt／md、附件與其他只下載。 */
export const DOWNLOAD_ONLY_KINDS = new Set(['office', 'attachment', 'other']);

const extOfName = (name) => {
  const m = /\.[^./\\]+$/.exec(String(name || ''));
  return m ? m[0].toLowerCase() : '';
};

/** 副檔名是否為可預覽點陣圖（JPG／PNG／WebP／BMP／GIF／TIFF）。 */
export const isImagePreviewable = (name) => IMAGE_PREVIEW_EXTS.has(extOfName(name));

/** Word／Excel／PowerPoint（含 OpenDocument、RTF、CSV）是否可轉 PDF 預覽。 */
export const isOfficePreviewable = (name) => OFFICE_PREVIEW_EXTS.has(extOfName(name));

/** 檢視器狀態列用的預覽名稱。 */
export const officePreviewLabel = (name) => {
  const ext = extOfName(name);
  if (['.doc', '.docx', '.odt', '.rtf'].includes(ext)) return 'Word 預覽';
  if (['.xls', '.xlsx', '.xlsm', '.ods', '.csv'].includes(ext)) return 'Excel 預覽';
  if (['.ppt', '.pptx', '.odp'].includes(ext)) return 'PowerPoint 預覽';
  return 'Office 預覽';
};

/** 可在檢視器開啟（CAD／模型／PDF／可預覽圖片／可轉 PDF 的 Office）。 */
export const opensInViewer = (item) => {
  if (item?.kind === 'image') return isImagePreviewable(item.name);
  if (item?.kind === 'office') return isOfficePreviewable(item.name);
  return VIEWABLE_KINDS.has(item?.kind);
};

/** 可分享給外包的檢視：2D 圖面、PDF、可預覽圖片、可轉 PDF 的 Office。不含 3D 與網頁。 */
export const isShareable = (item) => {
  const kind = item?.kind || kindOfExt(item?.ext);
  if (kind === 'model' || kind === 'web' || kind === 'attachment' || kind === 'other') return false;
  if (kind === 'drawing') return true;
  return supportsPageReview(item);
};

/** PDF、可預覽圖片，以及可轉 PDF 的 Office，使用頁碼比例座標釋疑。 */
export const supportsPageReview = (item) => {
  const kind = item?.kind || kindOfExt(item?.ext);
  if (kind === 'pdf') return true;
  if (kind === 'image') return isImagePreviewable(item?.name || '');
  if (kind === 'office') return isOfficePreviewable(item?.name || '');
  return false;
};
